import React, { Component } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { styled } from '@stitches/react';
import toast from 'react-hot-toast';
import ImageCropper, { WIDTHS } from './ImageCropper';

// The album's "about" document: Markdown text with embedded photos, videos and links.
// Stored as plain Markdown (albums/<id>/description), so it's safe to share with other clients,
// and always rendered through a sanitizer.

// Size limits for embedded files (checked here in the client; Storage rules aren't part of this repo).
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const formatMB = (bytes) => `${Math.round(bytes / (1024 * 1024))} MB`;

const VIDEO_EXT = /\.(mp4|m4v|mov|webm|ogv)(\?|#|$)/i;
const escapeAttr = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const renderer = new marked.Renderer();
// ![video](url) (or a link to a video file) embeds a player; everything else is an image.
// A trailing #w=50 on the address sets how wide it sits, as a % of the text column.
const IMAGE_WIDTH = /#w=(\d{1,3})$/;
export const imageWidth = (href) => {
  const m = IMAGE_WIDTH.exec(href || '');
  return m ? Math.min(100, Math.max(10, Number(m[1]))) : 100;
};
renderer.image = (href, title, text) => {
  if (!href) return escapeAttr(text);
  const width = imageWidth(href);
  const src = href.replace(IMAGE_WIDTH, '');
  const caption = title || '';
  const isVideo = (text || '').toLowerCase() === 'video' || VIDEO_EXT.test(src);
  const media = isVideo
    ? `<video src="${escapeAttr(src)}" controls preload="metadata"></video>`
    : `<img src="${escapeAttr(src)}" alt="${escapeAttr(text)}">`;
  const style = width < 100 ? ` style="width:${width}%"` : '';
  return `<figure${style}>${media}${caption ? `<figcaption>${escapeAttr(caption)}</figcaption>` : ''}</figure>`;
};

// The ![alt](url) image in `text` that `pos` is inside, if any: { start, end, alt, url, width }.
const IMAGE_MD = /!\[([^\]]*)\]\(([^)\s]+)\)/g;
const imageAt = (text, pos) => {
  IMAGE_MD.lastIndex = 0;
  let m;
  while ((m = IMAGE_MD.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    if (pos >= start && pos <= end) {
      if (m[1].toLowerCase() === 'video') return null;
      return { start, end, alt: m[1], url: m[2], width: imageWidth(m[2]) };
    }
  }
  return null;
};

if (!DOMPurify.__stewHooked) {
  DOMPurify.__stewHooked = true;
  // Links leave the app in a new tab.
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

export const renderMarkdown = (text) => DOMPurify.sanitize(
  marked.parse(text || '', { renderer, breaks: true, gfm: true, headerIds: false, mangle: false }),
  { ADD_TAGS: ['video', 'figure', 'figcaption'], ADD_ATTR: ['controls', 'preload', 'target'] },
);

const Doc = styled('div', {
  maxWidth: '75ch',
  overflowWrap: 'anywhere',
  '& > :first-child': { marginTop: 0 },
  '& h1, & h2, & h3, & h4': { margin: '24px 0 8px', lineHeight: 1.25 },
  '& h1': { fontSize: '1.5rem' },
  '& h2': { fontSize: '1.25rem' },
  '& h3, & h4': { fontSize: '1.05rem' },
  '& p, & ul, & ol, & blockquote, & pre': { margin: '0 0 16px' },
  '& blockquote': { padding: '4px 16px', borderLeft: '3px solid var(--placeholder-bg)', opacity: 0.85 },
  '& pre': { padding: '8px 16px', borderRadius: '8px', background: 'var(--placeholder-bg)', overflowX: 'auto' },
  '& code': { fontSize: '0.9em' },
  '& figure': { margin: '0 0 16px' },
  '& img, & video': { display: 'block', maxWidth: '100%', borderRadius: '8px' },
  '& figcaption': { marginTop: '4px', fontSize: '0.85em', opacity: 0.7 },
  '& a': { color: '#0d6efd' },
  '& hr': { margin: '24px 0' },
});

const Toolbar = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '4px',
  marginBottom: '8px',
  '& button': {
    minWidth: '32px', height: '32px', padding: '0 8px', border: '1px solid var(--edit-border)', borderRadius: '6px',
    background: 'var(--edit-bg)', color: 'inherit', fontSize: '14px', cursor: 'pointer',
  },
  '& button:hover:not(:disabled)': { background: 'var(--edit-bg-hover)', borderColor: 'var(--edit-accent)' },
  '& button:disabled': { opacity: 0.5, cursor: 'default' },
  '& button.active': { background: 'var(--edit-accent)', borderColor: 'var(--edit-accent)', color: '#fff' },
  '& .spacer': { flex: 1 },
  '& .sizes': { display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px' },
  '& .status': { fontSize: '12px', opacity: 0.7, padding: '0 8px' },
});

const Editor = styled('textarea', {
  display: 'block',
  width: '100%',
  minHeight: '224px',
  padding: '8px 16px',
  border: '1px solid var(--edit-border)',
  borderRadius: '8px',
  background: 'var(--edit-bg)',
  color: 'inherit',
  outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  '&:focus': { borderColor: 'var(--edit-accent)', boxShadow: '0 0 0 3px var(--edit-ring)' },
  resize: 'vertical',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '13px',
  lineHeight: 1.5,
});

const Preview = styled('div', {
  minHeight: '224px',
  padding: '8px 16px',
  border: '1px dashed var(--edit-border)',
  borderRadius: '8px',
  background: 'var(--edit-bg)',
});

export default class AlbumDetails extends Component {
  state = { draft: this.props.value || '', mode: 'write', uploading: 0, cropQueue: [], imageToken: null };

  textareaRef = React.createRef();
  fileInputRef = React.createRef();
  rootRef = React.createRef();
  selection = null;
  uploaded = 0; // files uploaded since the last save, so unused ones get cleaned up

  componentDidUpdate(prev) {
    // Pick up outside changes (a save, another device) unless there's an unsaved draft.
    if (prev.value !== this.props.value && this.state.draft === (prev.value || '')) {
      this.setState({ draft: this.props.value || '' });
    }
  }

  componentWillUnmount() {
    this.commit();
  }

  commit = () => {
    const { value, albumId, onSave } = this.props;
    if (this.state.draft !== (value || '') || this.uploaded > 0) {
      this.uploaded = 0;
      onSave(albumId, this.state.draft, value || '');
    }
  };

  handleBlur = (e) => {
    if (this.rootRef.current && e.relatedTarget && this.rootRef.current.contains(e.relatedTarget)) return;
    this.commit();
  };

  rememberSelection = () => {
    const el = this.textareaRef.current;
    if (!el) return;
    this.selection = [el.selectionStart, el.selectionEnd];
    // Offer the size buttons while the cursor is inside an embedded photo.
    const token = el.selectionStart === el.selectionEnd ? imageAt(el.value, el.selectionStart) : null;
    const old = this.state.imageToken;
    if ((token && token.url) !== (old && old.url) || (token && token.start) !== (old && old.start)) this.setState({ imageToken: token });
  };

  // Sets how wide the photo under the cursor sits in the text.
  setImageWidth = (width) => {
    const token = this.state.imageToken;
    if (!token) return;
    const base = token.url.replace(IMAGE_WIDTH, '');
    const url = width >= 100 ? base : `${base}#w=${width}`;
    const md = `![${token.alt}](${url})`;
    this.replace(md, { range: [token.start, token.end], selectStart: 2, selectEnd: 2 + token.alt.length });
  };

  // Replaces the selection (or `range`) and puts the cursor/selection where `select` says.
  replace = (text, { range, selectStart = text.length, selectEnd = text.length } = {}) => {
    const el = this.textareaRef.current;
    const [a, b] = range || this.selection || (el ? [el.selectionStart, el.selectionEnd] : [this.state.draft.length, this.state.draft.length]);
    const draft = this.state.draft.slice(0, a) + text + this.state.draft.slice(b);
    this.setState({ draft, mode: 'write' }, () => {
      const t = this.textareaRef.current;
      if (!t) return;
      t.focus();
      t.setSelectionRange(a + selectStart, a + selectEnd);
      this.rememberSelection();
    });
  };

  selected = () => {
    const [a, b] = this.selection || [this.state.draft.length, this.state.draft.length];
    return [a, b, this.state.draft.slice(a, b)];
  };

  wrap = (before, after, placeholder) => {
    const [, , sel] = this.selected();
    const text = sel || placeholder;
    this.replace(before + text + after, { selectStart: before.length, selectEnd: before.length + text.length });
  };

  // Prefixes every selected line (or toggles the prefix off when they all have it).
  prefixLines = (prefix, numbered) => {
    const { draft } = this.state;
    let [a, b] = this.selection || [draft.length, draft.length];
    const start = draft.lastIndexOf('\n', a - 1) + 1;
    let end = draft.indexOf('\n', b);
    if (end < 0) end = draft.length;
    const lines = draft.slice(start, end).split('\n');
    const has = (l) => (numbered ? /^\d+\.\s/.test(l) : l.startsWith(prefix));
    const all = lines.every(has);
    const next = lines.map((l, i) => {
      if (all) return numbered ? l.replace(/^\d+\.\s/, '') : l.slice(prefix.length);
      return (numbered ? `${i + 1}. ` : prefix) + l;
    }).join('\n');
    this.replace(next, { range: [start, end], selectStart: 0, selectEnd: next.length });
  };

  link = () => {
    const [, , sel] = this.selected();
    const url = window.prompt('Link address', 'https://');
    if (!url || url === 'https://') return;
    const label = sel || 'link text';
    this.replace(`[${label}](${url})`, { selectStart: 1, selectEnd: 1 + label.length });
  };

  // Videos upload straight away; JPG/PNG photos go through the crop & size dialog first.
  insertMedia = (files) => {
    const list = Array.from(files || []);
    const crops = [];
    list.forEach(file => {
      const isVideo = file.type.startsWith('video/');
      const isPhoto = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isVideo && !isPhoto) {
        toast.error(file.type.startsWith('image/')
          ? `${file.name || 'That image'} isn't supported. Photos must be JPG or PNG.`
          : 'Only JPG/PNG photos and videos can be embedded');
        return;
      }
      const limit = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
      if (file.size > limit) {
        toast.error(`${file.name || 'That file'} is too large (${formatMB(file.size)}). ${isVideo ? 'Videos' : 'Photos'} can be up to ${formatMB(limit)}.`);
        return;
      }
      if (isVideo) this.uploadAndInsert(file, { label: 'video' });
      else crops.push(file);
    });
    if (crops.length) this.setState(prev => ({ cropQueue: [...prev.cropQueue, ...crops] }));
  };

  // The photo at the front of the crop queue was cropped (blob) or skipped (no blob).
  finishCrop = async (blob, { width } = {}) => {
    const file = this.state.cropQueue[0];
    this.setState(prev => ({ cropQueue: prev.cropQueue.slice(1) }));
    if (!blob || !file) return;
    const label = (file.name || 'photo').replace(/\.[^.]+$/, '').replace(/[[\]()]/g, '');
    await this.uploadAndInsert(blob, { label, width, name: file.name });
  };

  uploadAndInsert = async (file, { label, width = 100, name }) => {
    this.setState(prev => ({ uploading: prev.uploading + 1 }));
    try {
      const { url } = await this.props.onUploadMedia(this.props.albumId, file);
      this.uploaded += 1;
      const md = `![${label}](${width < 100 ? `${url}#w=${width}` : url})`;
      const before = this.state.draft.slice(0, (this.selection || [this.state.draft.length])[0]);
      const lead = before && !before.endsWith('\n\n') ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
      this.replace(`${lead}${md}\n\n`);
    } catch (e) {
      toast.error(`Couldn't upload ${name || file.name || 'file'} ❌`);
    }
    this.setState(prev => ({ uploading: prev.uploading - 1 }));
  };

  onDrop = (e) => {
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
      e.preventDefault();
      this.insertMedia(e.dataTransfer.files);
    }
  };

  onPaste = (e) => {
    const files = e.clipboardData && e.clipboardData.files;
    if (files && files.length) {
      e.preventDefault();
      this.insertMedia(files);
    }
  };

  onKeyDown = (e) => {
    if (!(e.metaKey || e.ctrlKey)) return;
    const key = e.key.toLowerCase();
    if (key === 'b') { e.preventDefault(); this.rememberSelection(); this.wrap('**', '**', 'bold text'); }
    else if (key === 'i') { e.preventDefault(); this.rememberSelection(); this.wrap('*', '*', 'italic text'); }
    else if (key === 'k') { e.preventDefault(); this.rememberSelection(); this.link(); }
  };

  render() {
    const { editing, value } = this.props;
    const { draft, mode, uploading, cropQueue, imageToken } = this.state;

    if (!editing) return <Doc dangerouslySetInnerHTML={{ __html: renderMarkdown(value) }} />;

    // Keep focus (and the text selection) in the editor when a toolbar button is pressed.
    const keep = (e) => e.preventDefault();
    const tool = (label, title, onClick, extra) => (
      <button type="button" title={title} aria-label={title} onMouseDown={keep} onClick={onClick} disabled={mode === 'preview' || (uploading > 0 && extra === 'media')}>{label}</button>
    );

    return (
      <div ref={this.rootRef} onBlur={this.handleBlur}>
        <Toolbar>
          {tool(<b>B</b>, 'Bold (⌘B)', () => this.wrap('**', '**', 'bold text'))}
          {tool(<i>I</i>, 'Italic (⌘I)', () => this.wrap('*', '*', 'italic text'))}
          {tool('H', 'Heading', () => this.prefixLines('## '))}
          {tool('•', 'Bulleted list', () => this.prefixLines('- '))}
          {tool('1.', 'Numbered list', () => this.prefixLines('', true))}
          {tool('❝', 'Quote', () => this.prefixLines('> '))}
          {tool('🔗', 'Link (⌘K)', this.link)}
          {tool('🖼 Photo / video', 'Embed a photo (JPG/PNG) or video', () => this.fileInputRef.current.click(), 'media')}
          {mode === 'write' && imageToken && (
            <span className="sizes" title="How wide this photo sits in the text">
              <span className="status">Photo width</span>
              {WIDTHS.map(w => (
                <button key={w} type="button" className={imageToken.width === w ? 'active' : ''} onMouseDown={keep}
                  onClick={() => this.setImageWidth(w)}>{w}%</button>
              ))}
            </span>
          )}
          <span className="spacer" />
          {uploading > 0 && <span className="status">Uploading…</span>}
          <button type="button" className={mode === 'write' ? 'active' : ''} onMouseDown={keep} onClick={() => this.setState({ mode: 'write' })}>Write</button>
          <button type="button" className={mode === 'preview' ? 'active' : ''} onMouseDown={keep} onClick={() => this.setState({ mode: 'preview' })}>Preview</button>
        </Toolbar>
        {mode === 'write' ? (
          <Editor ref={this.textareaRef} value={draft} placeholder="Write about this album. Use the toolbar to add headings, links, photos and videos."
            onChange={(e) => { this.setState({ draft: e.target.value }); this.rememberSelection(); }}
            onSelect={this.rememberSelection} onKeyUp={this.rememberSelection} onClick={this.rememberSelection}
            onKeyDown={this.onKeyDown} onDrop={this.onDrop} onPaste={this.onPaste} onDragOver={(e) => e.preventDefault()} />
        ) : (
          <Preview>
            {draft.trim()
              ? <Doc dangerouslySetInnerHTML={{ __html: renderMarkdown(draft) }} />
              : <span className="text-muted">Nothing to preview yet.</span>}
          </Preview>
        )}
        <input ref={this.fileInputRef} type="file" accept="image/jpeg,image/png,video/*" multiple hidden
          onChange={(e) => { this.insertMedia(e.target.files); e.target.value = ''; }} />
        {cropQueue.length > 0 && (
          <ImageCropper key={`${cropQueue[0].name}-${cropQueue[0].size}-${cropQueue.length}`} file={cropQueue[0]}
            onSave={this.finishCrop} onCancel={() => this.finishCrop(null)} />
        )}
        <small className="text-muted d-block mt-1">Supports Markdown. Drop or paste JPG/PNG photos (up to 10 MB) and videos (up to 100 MB) into the editor to embed them. Put the cursor on a photo to change its width.</small>
      </div>
    );
  }
}
